// services/wrapped/exporter.ts
// Exporta o LibriTui Wrapped exatamente como ele aparece no app: mesmo drawFrame, mesma trilha e efeitos.
import { drawFrame, snapshotTime, TOTAL_DURATION, W, H, WrappedData, WrappedAssets } from './renderer';
import { WrappedAudioPlayer, WrappedAudioBuffers } from './audioEngine';
import type { MusicStyle } from './synth';

// MP4 primeiro (aceito direto por Instagram/WhatsApp/TikTok); WebM como reserva
const MIME_CANDIDATES = [
  'video/mp4;codecs=avc1.640028,mp4a.40.2',
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

export const pickVideoMime = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_CANDIDATES.find(m => MediaRecorder.isTypeSupported(m)) || null;
};

export interface ExportedVideo { blob: Blob; mime: string; ext: 'mp4' | 'webm' }

/**
 * Calibração de cor: alguns codificadores (ex.: H.264 do Chrome no Windows) gravam em "faixa limitada"
 * e os players expandem na reprodução, escurecendo os tons escuros. Gravamos meio segundo com dois cinzas
 * conhecidos, medimos o resultado (mostrado = a*v + b) e devolvemos a compensação inversa por quadro.
 */
type ColorComp = { mul: number; add: number } | null;
const colorCompCache: Record<string, ColorComp> = {};

const calibrateColor = async (mime: string): Promise<ColorComp> => {
  if (mime in colorCompCache) return colorCompCache[mime];
  let comp: ColorComp = null;
  try {
    // mesma resolução do vídeo final: o navegador pode escolher outro codificador para tamanhos pequenos
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const x = c.getContext('2d')!;
    const paint = () => { x.fillStyle = 'rgb(40,40,40)'; x.fillRect(0, 0, W / 2, H); x.fillStyle = 'rgb(200,200,200)'; x.fillRect(W / 2, 0, W / 2, H); };
    paint();
    const stream: MediaStream = (c as any).captureStream(30);
    const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
    const done = new Promise<void>(r => { rec.onstop = () => r(); });
    rec.start();
    const iv = window.setInterval(paint, 33);
    await new Promise(r => setTimeout(r, 600));
    window.clearInterval(iv);
    rec.stop();
    await done;
    stream.getTracks().forEach(t => t.stop());
    const vid = document.createElement('video');
    vid.muted = true;
    vid.src = URL.createObjectURL(new Blob(chunks, { type: mime.split(';')[0] }));
    await new Promise<void>((res, rej) => { vid.onloadeddata = () => res(); vid.onerror = () => rej(); });
    vid.currentTime = 0.3;
    await new Promise<void>(r => { vid.onseeked = () => r(); setTimeout(r, 1500); });
    const m = document.createElement('canvas');
    m.width = 64; m.height = 64;
    const mx = m.getContext('2d')!;
    mx.drawImage(vid, 0, 0, 64, 64);
    const lo = mx.getImageData(16, 32, 1, 1).data[1];
    const hi = mx.getImageData(48, 32, 1, 1).data[1];
    URL.revokeObjectURL(vid.src);
    const a = (hi - lo) / 160;
    const b = lo - a * 40;
    // só compensa distorções relevantes e do tipo "expansão" (a > 1), que dá para inverter com multiplicar + somar
    if (a > 1.03 && a < 1.4 && Math.abs(b) > 3) comp = { mul: 1 / a, add: -b / a };
  } catch {
    comp = null;
  }
  colorCompCache[mime] = comp;
  return comp;
};

const applyComp = (ctx: CanvasRenderingContext2D, comp: ColorComp) => {
  if (!comp) return;
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  const g = Math.round(255 * comp.mul);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = `rgb(${g},${g},${g})`;
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  const add = Math.max(0, Math.round(comp.add));
  if (add > 0) {
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = `rgb(${add},${add},${add})`;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  }
  ctx.restore();
};

export const exportWrappedVideo = (
  data: WrappedData,
  assets: WrappedAssets,
  buffers: WrappedAudioBuffers,
  style: MusicStyle,
  onProgress: (p: number) => void,
): Promise<ExportedVideo> => {
  const mime = pickVideoMime();
  if (!mime) return Promise.reject(new Error('Gravação de vídeo não suportada neste navegador.'));
  return calibrateColor(mime).then(comp => recordVideo(mime, comp, data, assets, buffers, style, onProgress));
};

const recordVideo = (
  mime: string,
  comp: ColorComp,
  data: WrappedData,
  assets: WrappedAssets,
  buffers: WrappedAudioBuffers,
  style: MusicStyle,
  onProgress: (p: number) => void,
): Promise<ExportedVideo> => new Promise((resolve, reject) => {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx || !(canvas as any).captureStream) { reject(new Error('Canvas sem suporte a captura.')); return; }
  drawFrame(ctx, 0, data, assets);
  applyComp(ctx, comp);

  const video: MediaStream = (canvas as any).captureStream(30);
  const audio = new WrappedAudioPlayer(buffers, style, true);
  const stream = new MediaStream([...video.getVideoTracks(), ...(audio.stream?.stream.getAudioTracks() || [])]);
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000, audioBitsPerSecond: 192_000 });
  const chunks: Blob[] = [];
  let timer: number | undefined;

  const cleanup = () => {
    if (timer) window.clearInterval(timer);
    audio.dispose();
    stream.getTracks().forEach(tr => tr.stop());
  };

  recorder.ondataavailable = e => { if (e.data && e.data.size > 0) chunks.push(e.data); };
  recorder.onerror = () => { cleanup(); reject(new Error('Falha ao gravar o vídeo.')); };
  recorder.onstop = () => {
    cleanup();
    const type = mime.split(';')[0];
    resolve({ blob: new Blob(chunks, { type }), mime: type, ext: type === 'video/mp4' ? 'mp4' : 'webm' });
  };

  recorder.start(250);
  audio.play(0).then(() => {
    // o relógio do áudio comanda os quadros: som e imagem ficam alinhados no arquivo
    timer = window.setInterval(() => {
      const t = audio.time();
      drawFrame(ctx, t, data, assets);
      applyComp(ctx, comp);
      onProgress(Math.min(1, t / TOTAL_DURATION));
      if (t >= TOTAL_DURATION + 0.25 && recorder.state === 'recording') recorder.stop();
    }, 1000 / 30);
  });
});

/** Imagem 1080x1920 de uma cena, no instante em que tudo já entrou (idêntica ao que o player mostra). */
export const renderSceneImage = (sceneIndex: number, data: WrappedData, assets: WrappedAssets): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error('Canvas indisponível')); return; }
    drawFrame(ctx, snapshotTime(sceneIndex), data, assets);
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Falha ao gerar a imagem'))), 'image/png');
  });

/** Compartilha pelo menu nativo do aparelho; se não der, baixa o arquivo. */
export const shareOrDownload = async (blob: Blob, filename: string, title: string, preferShare: boolean) => {
  const file = new File([blob], filename, { type: blob.type });
  if (preferShare && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title, text: 'Minha retrospectiva no libritui.' });
      return 'shared' as const;
    } catch (e: any) {
      if (e?.name === 'AbortError') return 'cancelled' as const;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return 'downloaded' as const;
};
